import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
} from '@material-ui/core';

import {
  CardWrapper,
  GraphQLErrorWrapper,
  Loader,
  SuccessSnackBar,
  CustomButton,
} from '../../generic';

import {
  FTPExportIntegrationForm,
  FTPExportIntegrationTestingCard,
} from '../../exportIntegration/components';

import {
  GROUP_QUERY,
  GROUP_UPDATE_FTP_EXPORT_INTEGRATION_MUTATION,
  GROUP_PROPAGATE_FTP_EXPORT_INTEGRATION_MUTATION,
} from '../queries';

const GroupFTPExportIntegrationEditCard = (props) => {
  const {
    group,
  } = props;

  const { t } = useTranslation('translations');
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [askConfirmPropagateVisible, setAskConfirmPropagateVisible] = React.useState(false);

  const [
    groupUpdateFTPExportIntegrationMutationAction,
    {
      loading,
      error,
    },
  ] = useMutation(GROUP_UPDATE_FTP_EXPORT_INTEGRATION_MUTATION, {
    refetchQueries: () => {
      const result = [
        {
          query: GROUP_QUERY,
          variables: {
            id: group.id,
          },
        },
      ];
      return result;
    },
    awaitRefetchQueries: true,
  });

  const [
    groupPropagateFTPExportIntegrationMutationAction,
    {
      loading: loadingPropagate,
      error: errorPropagate,
    },
  ] = useMutation(GROUP_PROPAGATE_FTP_EXPORT_INTEGRATION_MUTATION, {
    refetchQueries: () => {
      const result = [
        {
          query: GROUP_QUERY,
          variables: {
            id: group.id,
          },
        },
      ];
      return result;
    },
    awaitRefetchQueries: true,
  });

  const handleFormSubmit = async (data) => {
    try {
      const result = await groupUpdateFTPExportIntegrationMutationAction({
        variables: {
          input: {
            id: group.id,
            ...data,
          },
        },
      });
      if (result) setSuccessOpen(true);
    }
    catch (e) { } // eslint-disable-line
  };

  const handlePropagate = async () => {
    try {
      const result = await groupPropagateFTPExportIntegrationMutationAction({
        variables: {
          input: {
            id: group.id,
          },
        },
      });
      if (result) setSuccessOpen(true);
      setAskConfirmPropagateVisible(false);
    }
    catch (e) { } // eslint-disable-line
  };

  return (
    <>
      <CardWrapper
        cardTitle={t('exportIntegrations.ftp.edit.title')}
        cardBody={
          (
            <>
              <SuccessSnackBar open={successOpen} setOpen={setSuccessOpen} message="common.operationSuccess" />
              <GraphQLErrorWrapper error={error || errorPropagate} />
              <Loader loading={loading || loadingPropagate} />
              <FTPExportIntegrationForm
                ftpExportIntegration={group.ftpExportIntegration}
                onFormSubmit={handleFormSubmit}
              />
              <div style={{ width: '100%', marginTop: 20 }}>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={() => setAskConfirmPropagateVisible(true)}
                >
                  <Typography>{t('common.propagateConfig')}</Typography>
                </Button>
                <Dialog
                  fullWidth
                  maxWidth="sm"
                  open={askConfirmPropagateVisible}
                  onClose={() => setAskConfirmPropagateVisible(false)}
                >
                  <DialogTitle id="draggable-dialog-title">
                    {t('common.confirmPropagateConfig')}
                  </DialogTitle>
                  <DialogActions>
                    <CustomButton autoFocus onClick={() => setAskConfirmPropagateVisible(false)} color="info">
                      {t('common.cancel')}
                    </CustomButton>
                    <CustomButton onClick={() => handlePropagate()} color="primary">
                      {t('common.ok')}
                    </CustomButton>
                  </DialogActions>
                </Dialog>
              </div>
            </>
          )
        }
      />
      <FTPExportIntegrationTestingCard
        id={group.id}
        timezone={group.timezone}
      />
    </>
  );
};

const propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string,
    timezone: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    ftpExportIntegration: PropTypes.object,
  }).isRequired,
};

GroupFTPExportIntegrationEditCard.propTypes = propTypes;

export default GroupFTPExportIntegrationEditCard;
